import {
  CartesianGrid,
  DefaultZIndexes,
  Line,
  LineChart as RechartLineChart,
  ReferenceArea,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { DotItemDotProps, YAxisProps } from 'recharts';
import { useTheme } from 'styled-components';

import { font_sizes } from '@hookdeck/theme';

import Chart, {
  ChartDataSet,
  ChartProps,
  ChartSampleCursor,
  CLASS_PREFIX,
  formatActiveNameForDom,
} from './Chart';
import { formatChartAxisNumber, getYAxisTicks } from './format';

const DASHED_LINE = '4 8';
const SPARSE_DATA_DOT_RADIUS = 2;
const VALUE_LINE_Z_INDEX = DefaultZIndexes.axis + 1;

const getFiniteDataPointCount = (dataset: ChartDataSet) =>
  dataset.data.filter((data) => Number.isFinite(Number(data.y))).length;

const isFiniteChartValue = (value: string | number | null | undefined) =>
  Number.isFinite(Number(value));

const LineChart: React.FC<Omit<ChartProps, 'type' | 'children'>> = (props) => {
  const theme = useTheme();

  const allow_decimals = !props.datasets?.some((dataset) =>
    dataset.data.some((data) => Number(data.y) > 1),
  );

  return (
    <Chart type={'line'} {...props}>
      {({
        id,
        data,
        cached_datasets,
        datasets,
        hovered,
        priority_display,
        active_name,
        setHovered,
        sync_id,
        selection_range,
        onChartMouseDown,
        onChartMouseMove,
      }) => {
        const highest_data_value =
          cached_datasets?.map((d) => d.highest).reduce((a, b) => Math.max(a, b), 0) || 0;
        const y_axis_ticks = getYAxisTicks({
          data_max: highest_data_value,
          allow_decimals,
        });
        const y_axis_domain: YAxisProps['domain'] = [
          y_axis_ticks[0],
          y_axis_ticks[y_axis_ticks.length - 1],
        ];
        const y_axis_width_value = y_axis_ticks.reduce(
          (width, tick) => Math.max(width, formatChartAxisNumber(tick).length),
          1,
        );
        const highest_value_line_z_index = VALUE_LINE_Z_INDEX + (datasets?.length ?? 0);

        return (
          <RechartLineChart
            margin={{ top: 0, left: 0, right: 0, bottom: 0 }}
            data={data}
            syncId={sync_id}
            syncMethod="index"
            accessibilityLayer={false}
            onMouseMove={(e) => {
              if (active_name === null && !e) {
                return;
              }
              onChartMouseMove(e);
            }}
            onMouseDown={onChartMouseDown}>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={theme.colors.outline.neutral}
              horizontalCoordinatesGenerator={({ yAxis }) => {
                return y_axis_ticks
                  .map((tick) => yAxis?.scale?.map(tick))
                  .filter((coordinate): coordinate is number => Number.isFinite(coordinate));
              }}
              vertical={false}
            />
            <Tooltip
              cursor={<ChartSampleCursor stroke={theme.colors.outline.neutral_3} />}
              content={() => null}
            />
            <XAxis
              dataKey={'name'}
              allowDecimals={false}
              stroke={theme.colors.outline.neutral}
              tick={{
                fill: theme.colors.on.neutral.tertiary_neutral,
                fontSize: theme.font_sizes.xs.font_size,
              }}
              tickMargin={8}
              minTickGap={16}
              ticks={priority_display}
            />
            <YAxis
              width={32 + y_axis_width_value * 8}
              allowDecimals={allow_decimals}
              stroke={'transparent'}
              domain={y_axis_domain}
              ticks={y_axis_ticks}
              interval={0}
              tickMargin={8}
              minTickGap={24}
              tick={{
                fill: theme.colors.on.neutral.tertiary_neutral,
                fontSize: font_sizes.xs.font_size,
              }}
              tickFormatter={formatChartAxisNumber}
            />
            {selection_range && (
              <ReferenceArea
                x1={selection_range.start}
                x2={selection_range.end}
                fill={theme.colors.surface.base.hover.neutral}
                strokeOpacity={0}
              />
            )}
            {datasets?.flatMap((dataset, dataset_index) => {
              const stroke = dataset.theme_color
                ? theme.colors.surface.chart[dataset.theme_color]
                : dataset.hex_color;
              const z_index =
                hovered?.key === dataset.key
                  ? highest_value_line_z_index + 1
                  : highest_value_line_z_index - dataset_index;
              const show_sparse_data_dots = getFiniteDataPointCount(dataset) <= 3;
              const dot = ({ cx, cy, index, points, value }: DotItemDotProps) => {
                let segment_start_index = index;
                let segment_end_index = index;

                while (isFiniteChartValue(points[segment_start_index - 1]?.value)) {
                  segment_start_index -= 1;
                }

                while (isFiniteChartValue(points[segment_end_index + 1]?.value)) {
                  segment_end_index += 1;
                }

                const is_short_isolated_segment = segment_end_index - segment_start_index + 1 <= 2;

                if (
                  !isFiniteChartValue(value) ||
                  !Number.isFinite(cx) ||
                  !Number.isFinite(cy) ||
                  (!show_sparse_data_dots && (dataset.connect_nulls || !is_short_isolated_segment))
                ) {
                  return <circle cx={0} cy={0} r={0} fill="none" stroke="none" />;
                }

                return (
                  <circle
                    cx={cx}
                    cy={cy}
                    r={SPARSE_DATA_DOT_RADIUS}
                    fill={stroke}
                    stroke={stroke}
                  />
                );
              };
              const line_props = {
                className: `${CLASS_PREFIX}-${id} ${CLASS_PREFIX}-${id}-${formatActiveNameForDom(
                  dataset.key,
                )}`,
                type: 'linear' as const,
                dataKey: dataset.label,
                stroke,
                zIndex: z_index,
                isAnimationActive: false,
                dot: false,
                connectNulls: dataset.connect_nulls,
              };

              if (hovered && hovered.key !== dataset.key) {
                return (
                  <Line
                    key={`${dataset.key}-muted`}
                    type={'linear'}
                    strokeWidth={2}
                    dataKey={dataset.label}
                    stroke={theme.colors.outline.neutral}
                    zIndex={z_index}
                    isAnimationActive={false}
                    dot={false}
                    connectNulls={dataset.connect_nulls}
                  />
                );
              }

              return [
                !dataset.connect_nulls && (
                  <Line
                    key={`${dataset.key}-connected`}
                    {...line_props}
                    strokeWidth={2}
                    connectNulls
                    style={{ transition: 'stroke-opacity 300ms', opacity: 0.5 }}
                    strokeDasharray={DASHED_LINE}
                    strokeLinecap={'round'}
                    strokeLinejoin={'round'}
                  />
                ),
                <Line
                  key={dataset.key}
                  {...line_props}
                  strokeWidth={2}
                  onMouseEnter={() =>
                    setHovered({
                      key: dataset.key,
                      label: dataset.label,
                      ...(active_name ? { name: active_name } : {}),
                    })
                  }
                  onMouseLeave={() => setHovered(null)}
                  activeDot={
                    active_name
                      ? {
                          onMouseEnter: () =>
                            setHovered({
                              key: dataset.key,
                              label: dataset.label,
                              name: active_name,
                            }),
                          onMouseLeave: () => setHovered(null),
                        }
                      : false
                  }
                  style={{ transition: 'stroke-opacity 300ms' }}
                  {...(dataset.dashed
                    ? {
                        dot: false,
                        strokeDasharray: DASHED_LINE,
                        strokeLinecap: 'round',
                        strokeLinejoin: 'round',
                      }
                    : {
                        dot,
                      })}
                />,
              ];
            })}
          </RechartLineChart>
        );
      }}
    </Chart>
  );
};

export default LineChart;
